import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart, ReferenceLine,
} from "recharts";
import {
  Plus, Trash2, PlayCircle, Save, Sparkles, Loader2, TrendingUp, DollarSign, Percent, Coins, Wallet, Copy, Download, FileText, Calendar, Wand2, ArrowRight, History, RotateCcw, Target, Lock, AlertTriangle,
} from "lucide-react";
import { buildCsv, downloadBlob } from "@/lib/csv";
import { renderMarkdown } from "@/lib/markdown";
import { TEST_IDS } from "@/lib/testIds";
import TickerAutocomplete from "@/components/TickerAutocomplete";
import { OptimizerCard } from "@/components/builder/OptimizerCard";
import { SnapshotHistory } from "@/components/builder/SnapshotHistory";
import { IncomeTargetCard } from "@/components/builder/IncomeTargetCard";
import { incomeBasis } from "@/lib/incomePlanner";
import { fmt$, fmt$2, fmtPct } from "@/lib/format";
import usePageSeo from "@/hooks/usePageSeo";

const STARTER = {
  name: "Dividend Starter",
  description: "Core dividend ETFs blend",
  holdings: [
    { ticker: "SCHD", weight: 50 },
    { ticker: "VYM", weight: 30 },
    { ticker: "JEPI", weight: 20 },
  ],
  initial_investment: 10000,
  start_date: "2015-01-01",
  end_date: new Date().toISOString().slice(0, 10),
  drip: true,
  additional_contribution: 0,
  contribution_frequency: "monthly",
};

// Formatters live in /app/frontend/src/lib/format.js (imported above)

const BACKTEST_CALC_VERSION = 3; // keep in sync with backend/backtest.py CALC_VERSION

export default function PortfolioBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const { sub } = useSubscription() || {};
  const isProPlan = !!user?.is_admin || sub?.plan === "pro";
  usePageSeo({
    title: id ? "Edit portfolio — Dividend Portfolio Planner" : "Build a dividend portfolio — Dividend Portfolio Planner",
    description: "Build a dividend portfolio in under a minute. Pick tickers, drag weights, toggle DRIP, add DCA, and backtest 30+ years.",
    path: id ? `/portfolio/${id}` : "/portfolio/new",
    // `/portfolio/new` is public and indexable; `/portfolio/:id` is a private
    // per-user resource — never index those individual edit URLs.
    noindex: !!id,
  });
  // Anonymous visitors can build a portfolio and run unlimited free
  // backtests — the account-creation prompt is deferred until they try to
  // *save* the result. See task 5 (2026-09-10): "introduce account creation
  // when the visitor saves results".
  const requireAuth = (label) => {
    // Persist the current form so the visitor lands back on the builder
    // with their tickers still filled in after signup. Only used on the
    // anonymous /portfolio/new path (no `id`).
    try {
      if (!id) localStorage.setItem("dp_anon_portfolio", JSON.stringify(form));
    } catch (_) { /* localStorage may be disabled */ }
    toast.error(`Create a free account to ${label}`, {
      action: {
        label: "Create free account",
        onClick: () => nav(`/auth?next=${encodeURIComponent(loc.pathname)}&intent=save-portfolio`),
      },
      duration: 8000,
    });
  };
  const [searchParams] = useSearchParams();
  // Support ?seed=SCHD or ?seed=SCHD,VYM,JEPI to pre-seed the ticker(s) — used by
  // the Research page "Add to new portfolio" button. Only applies for /portfolio/new.
  const seedInitial = useMemo(() => {
    if (id) return null;
    const raw = searchParams.get("seed") || "";
    const tickers = raw.split(/[,\s]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
    if (tickers.length === 0) return null;
    // Equal weights, rounded to 2 dp, last one absorbs drift.
    const per = Number((100 / tickers.length).toFixed(2));
    const holdings = tickers.map((t, i, a) => ({
      ticker: t,
      weight: i === a.length - 1 ? Number((100 - per * (a.length - 1)).toFixed(2)) : per,
    }));
    return {
      ...STARTER,
      name: tickers.length === 1 ? `${tickers[0]} portfolio` : `${tickers.slice(0, 3).join(" + ")}${tickers.length > 3 ? "…" : ""}`,
      description: `Seeded from Research`,
      holdings,
    };
  }, [id, searchParams]);
  const [form, setForm] = useState(() => {
    // Priority order for /portfolio/new initial state:
    //   1. ?seed=CSV in the URL — the user just arrived from the AI Advisor,
    //      Compare, or Research and EXPECTS to see those tickers. Wins over
    //      any stashed anon draft (previously stashed draft always beat the
    //      seed, so "Add all to scenario" appeared broken to the user).
    //   2. Stashed anon draft from an earlier session (bounced through
    //      /auth?intent=save-portfolio) — restored so work isn't lost.
    //   3. STARTER (empty portfolio).
    if (!id) {
      if (seedInitial) return seedInitial;
      try {
        const stashed = localStorage.getItem("dp_anon_portfolio");
        if (stashed) {
          const parsed = JSON.parse(stashed);
          if (parsed && Array.isArray(parsed.holdings)) return parsed;
        }
      } catch (_) { /* ignore malformed cache */ }
    }
    return seedInitial || STARTER;
  });

  // Post-signup return: if the visitor came back with `intent=save-portfolio`
  // and we successfully restored the tickers, greet them so they know their
  // work wasn't lost and prompt them to save now that they're authed. Also
  // offers the 7-day Core trial dialog (2026-02-13) since first-portfolio
  // save is the moment they're most likely to say yes to more capacity.
  const [showTrialOffer, setShowTrialOffer] = useState(false);
  useEffect(() => {
    if (id) return;
    if (searchParams.get("intent") === "save-portfolio" && user) {
      toast.success("Welcome back — your portfolio is right where you left it. Save it to your dashboard whenever you're ready.");
      // Only prompt once per account — the trial CTA is idempotent but a
      // repeat dialog on every reload would be nagging. Uses the visitor's
      // user id so the flag survives log-in-as-different-user churn.
      const flagKey = `dp_trial_offered_${user?.id || user?.email || "anon"}`;
      try {
        if (!localStorage.getItem(flagKey)) {
          // Small delay so the welcome toast lands first and the dialog
          // doesn't stomp on the visitor's mental model of the page.
          setTimeout(() => {
            setShowTrialOffer(true);
            try { localStorage.setItem(flagKey, "1"); } catch (_) {}
          }, 600);
        }
      } catch (_) { /* localStorage disabled — silently skip the dialog */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const [newTicker, setNewTicker] = useState("");
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content, streaming?}
  const [followUp, setFollowUp] = useState("");
  const [explaining, setExplaining] = useState(false);
  const streamRef = useRef(null);
  const [snapshots, setSnapshots] = useState([]);

  const loadSnapshots = useCallback(async () => {
    if (!id) { setSnapshots([]); return; }
    try {
      const res = await api.get(`/portfolios/${id}/snapshots`);
      setSnapshots(res.data.snapshots || []);
    } catch {
      // silent — no-op for unsaved portfolios
    }
  }, [id]);

  useEffect(() => { loadSnapshots(); }, [loadSnapshots]);

  // Saved workspace: last backtest, AI explanation thread, optimizer proposal and income-planner state.
  const [seedVersion, setSeedVersion] = useState(0);
  const [resultSig, setResultSig] = useState(null);
  const optimizerRef = useRef(null);
  const plannerRef = useRef(null);
  const outdatedRef = useRef(false);
  const saveWorkspace = useCallback((patch, pid = id) => {
    if (!pid) return;
    api.put(`/portfolios/${pid}/workspace`, patch).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/portfolios/${id}/workspace`).then((r) => {
      const ws = r.data.workspace || {};
      optimizerRef.current = ws.optimizer || null;
      plannerRef.current = ws.income_target || null;
      if (ws.backtest) {
        setResult(ws.backtest);
        setResultSig(ws.inputs_sig || null);
        setMessages(ws.messages || []);
        // Saved with older backtest math → refresh once the form has loaded.
        if ((ws.backtest.calc_version || 0) < BACKTEST_CALC_VERSION) outdatedRef.current = true;
      }
      setSeedVersion((v) => v + 1);
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/portfolios/${id}`).then((r) => setForm({
      // Fall back the new DCA fields for portfolios saved before the feature
      // shipped so the form UI never renders `undefined` in the inputs.
      additional_contribution: 0,
      contribution_frequency: "monthly",
      ...r.data,
    })).catch(() => toast.error("Portfolio not found"));
  }, [id]);

  // Typewriter effect: reveals `fullText` into the last assistant message so long
  // Claude answers feel instant. Emergent LlmChat doesn't expose token-streaming,
  // so we stream client-side once the full response arrives.
  const streamIntoLastMessage = (fullText) => {
    if (streamRef.current) clearInterval(streamRef.current);
    let idx = 0;
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], content: "", streaming: true };
      return next;
    });
    streamRef.current = setInterval(() => {
      const chunk = Math.floor(2 + Math.random() * 6);
      idx = Math.min(idx + chunk, fullText.length);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], content: fullText.slice(0, idx) };
        return next;
      });
      if (idx >= fullText.length) {
        clearInterval(streamRef.current);
        streamRef.current = null;
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { ...next[next.length - 1], streaming: false };
          return next;
        });
      }
    }, 20);
  };

  useEffect(() => () => { if (streamRef.current) clearInterval(streamRef.current); }, []);

  // Dynamic follow-up suggestions built from actual holdings.
  const followUpSuggestions = useMemo(() => {
    if (!form.holdings || form.holdings.length === 0) return [];
    const sorted = [...form.holdings].sort((a, b) => Number(b.weight) - Number(a.weight));
    const largest = sorted[0].ticker;
    return [
      `What if I remove ${largest}?`,
      "Do you want me to optimize my returns based on my selections?",
      "How risky is this portfolio?",
    ];
  }, [form.holdings]);

  const copyMessage = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — select the text manually");
    }
  };

  const safeSlug = (form.name || "portfolio").replace(/[^a-z0-9-_]+/gi, "_").toLowerCase();
  const today = new Date().toISOString().slice(0, 10);

  const exportResultsCsv = () => {
    if (!result) return;
    const s = result.summary;
    const summaryRows = [
      ["metric", "value"],
      ["initial_investment", s.initial_investment],
      ["final_value", s.final_value],
      ["total_return_pct", s.total_return_pct],
      ["cagr_pct", s.cagr_pct],
      ["total_dividends", s.total_dividends],
      ["yield_on_cost_pct", s.yield_on_cost_pct],
      ["years", s.years],
      ["drip", s.drip ? "yes" : "no"],
    ];
    const seriesRows = [
      ["date", "value", "dividends_paid", "cumulative_dividends"],
      ...result.series.map((p) => [p.date, p.value, p.dividends_paid, p.cumulative_dividends]),
    ];
    const contribRows = [
      ["ticker", "shares", "current_value", "total_dividends", "weight_pct"],
      ...result.contributions.map((c) => [c.ticker, c.shares, c.current_value, c.total_dividends, c.weight]),
    ];
    const csv = [
      "# SUMMARY",
      buildCsv(summaryRows[0], summaryRows.slice(1)),
      "",
      "# PER-TICKER CONTRIBUTIONS",
      buildCsv(contribRows[0], contribRows.slice(1)),
      "",
      "# TIME SERIES",
      buildCsv(seriesRows[0], seriesRows.slice(1)),
    ].join("\n");
    downloadBlob(csv, `dpp-${safeSlug}-backtest-${today}.csv`);
    toast.success("Backtest results downloaded");
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const reportRef = useRef(null);
  const coverRef = useRef(null);

  const exportReportPdf = async () => {
    if (!result || !reportRef.current) return;
    setDownloadingPdf(true);
    // Reveal the cover page ONLY while html2canvas snapshots the DOM.
    const cover = coverRef.current;
    const originalDisplay = cover?.style.display;
    if (cover) cover.style.display = "block";
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `dpp-${safeSlug}-report-${today}.pdf`,
          image: { type: "jpeg", quality: 0.94 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#F7F5F0" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"], before: ".pdf-page-break" },
        })
        .from(reportRef.current)
        .save();
      toast.success("PDF report downloaded");
    } catch (err) {
      toast.error(`PDF export failed: ${err.message || err}`);
    } finally {
      if (cover) cover.style.display = originalDisplay || "none";
      setDownloadingPdf(false);
    }
  };

  const totalWeight = useMemo(() => form.holdings.reduce((s, h) => s + Number(h.weight || 0), 0), [form.holdings]);
  const weightsValid = Math.abs(totalWeight - 100) < 0.01 && form.holdings.length > 0;

  // Redistribute weights so they sum to 100 with 2-decimal precision.
  // Rounding drift is absorbed into the last holding, guaranteeing exact 100.
  const balanceTo100 = (holdings) => {
    if (holdings.length === 0) return holdings;
    const per = 100 / holdings.length;
    const rounded = Number(per.toFixed(2));
    return holdings.map((h, i, arr) => ({
      ...h,
      weight: i === arr.length - 1
        ? Number((100 - rounded * (arr.length - 1)).toFixed(2))
        : rounded,
    }));
  };

  const addTicker = () => {
    const t = newTicker.trim().toUpperCase();
    if (!t) return;
    if (form.holdings.some((h) => h.ticker === t)) { toast.error("Already added"); return; }
    // Auto-balance: give the new ticker an equal share and redistribute all others.
    const next = balanceTo100([...form.holdings, { ticker: t, weight: 0 }]);
    setForm({ ...form, holdings: next });
    setNewTicker("");
  };

  const setWeight = (i, w) => {
    const holdings = [...form.holdings];
    holdings[i] = { ...holdings[i], weight: Number(w) };
    setForm({ ...form, holdings });
  };

  // Drag-to-rebalance: dragging one slider proportionally scales the OTHER holdings
  // so the total always sums to 100. If a single ticker is present, its slider is fixed at 100.
  const setWeightSlider = (i, next) => {
    const holdings = form.holdings;
    if (holdings.length === 0) return;
    next = Math.max(0, Math.min(100, Number(next)));
    if (holdings.length === 1) {
      setForm({ ...form, holdings: [{ ...holdings[0], weight: 100 }] });
      return;
    }
    const othersTotal = holdings.reduce((s, h, idx) => idx === i ? s : s + Number(h.weight || 0), 0);
    const remaining = 100 - next;
    let updated;
    if (othersTotal <= 0.001) {
      // no "room" to rebalance from — spread `remaining` equally across others
      const share = remaining / (holdings.length - 1);
      updated = holdings.map((h, idx) => idx === i ? { ...h, weight: next } : { ...h, weight: share });
    } else {
      const scale = remaining / othersTotal;
      updated = holdings.map((h, idx) => idx === i
        ? { ...h, weight: next }
        : { ...h, weight: Number(h.weight || 0) * scale });
    }
    // 2-decimal rounding with drift absorbed into the last "other" holding.
    const rounded = updated.map((h) => ({ ...h, weight: Number(Number(h.weight).toFixed(2)) }));
    const drift = Number((100 - rounded.reduce((s, h) => s + h.weight, 0)).toFixed(2));
    if (drift !== 0) {
      const lastOtherIdx = [...rounded.keys()].reverse().find((idx) => idx !== i);
      if (lastOtherIdx !== undefined) {
        rounded[lastOtherIdx] = { ...rounded[lastOtherIdx], weight: Number((rounded[lastOtherIdx].weight + drift).toFixed(2)) };
      }
    }
    setForm({ ...form, holdings: rounded });
  };

  const removeHolding = (i) => {
    const holdings = balanceTo100(form.holdings.filter((_, idx) => idx !== i));
    setForm({ ...form, holdings });
  };

  const [runError, setRunError] = useState(null);

  const toPayload = (f) => ({
    name: f.name || "Untitled",
    description: f.description || "",
    holdings: f.holdings,
    initial_investment: Number(f.initial_investment),
    start_date: f.start_date,
    end_date: f.end_date,
    drip: !!f.drip,
    additional_contribution: Number(f.additional_contribution) || 0,
    contribution_frequency: f.contribution_frequency || "monthly",
  });
  const inputsSig = (f) => {
    const { name, description, ...rest } = toPayload(f);
    return JSON.stringify({ ...rest, holdings: (rest.holdings || []).map((h) => [h.ticker, Number(h.weight)]) });
  };
  const resultsStale = !!(result && resultSig && resultSig !== inputsSig(form));

  // Persist the AI explanation thread once streaming settles.
  useEffect(() => {
    if (!id || !result || !isProPlan || explaining || messages.some((m) => m.streaming)) return;
    const t = setTimeout(() => saveWorkspace({ messages }), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, explaining]);

  const runBacktest = async (opts = {}) => {
    const f = opts.formOverride || form;
    const valid = Math.abs(f.holdings.reduce((s, h) => s + Number(h.weight || 0), 0) - 100) < 0.01 && f.holdings.length > 0;
    if (!valid) { toast.error("Weights must sum to 100%"); return; }
    setRunning(true); setResult(null); setMessages([]); setFollowUp(""); setRunError(null);
    try {
      const payload = toPayload(f);
      const url = opts.refresh ? "/backtest?refresh=1" : "/backtest";
      const res = await api.post(url, payload);
      const sig = inputsSig(f);
      setResult(res.data);
      setResultSig(sig);
      saveWorkspace({ backtest: res.data, messages: [], inputs_sig: sig });
      if (!opts.silent) toast.success(res.data.cached ? "Loaded cached result" : "Backtest complete");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Backtest failed — please try again";
      setRunError(msg);
      toast.error(msg);
    } finally { setRunning(false); }
  };

  useEffect(() => {
    if (!outdatedRef.current || !id || form?.id !== id) return;
    outdatedRef.current = false;
    toast.message("Updating results to the latest backtest method…");
    runBacktest({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedVersion, form?.id, id]);

  // Income planner → portfolio form: fill the amount, save the portfolio, re-run the backtest.
  const [applyingPlan, setApplyingPlan] = useState(false);
  const applyPlannerAmount = async (patch, label) => {
    const next = { ...form, ...patch };
    setForm(next);
    setApplyingPlan(true);
    try {
      if (id) await api.put(`/portfolios/${id}`, toPayload(next));
      await runBacktest({ formOverride: next, silent: true });
      toast.success(`${label} applied${id ? " and saved" : ""} — backtest updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply amount");
    } finally {
      setApplyingPlan(false);
    }
  };

  // Anonymous-visitor save handler — bypasses the toast-action gate and
  // routes the visitor straight to Create-Account mode on /auth so the
  // moment of highest conversion intent is a single click (2026-02-13).
  const redirectToAccountSetup = () => {
    try {
      if (!id) localStorage.setItem("dp_anon_portfolio", JSON.stringify(form));
    } catch (_) { /* localStorage may be disabled */ }
    toast("Create your free account to save this portfolio", {
      description: "Your tickers and settings are stashed — we'll bring them right back.",
      duration: 4000,
    });
    nav(`/auth?mode=register&next=${encodeURIComponent(loc.pathname)}&intent=save-portfolio`);
  };

  const save = async () => {
    if (!user) { redirectToAccountSetup(); return; }
    if (!weightsValid) { toast.error("Weights must sum to 100%"); return; }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (id) {
        await api.put(`/portfolios/${id}`, payload);
      } else {
        const res = await api.post("/portfolios", payload);
        // Carry over any results produced before the first save.
        if (result) {
          saveWorkspace({
            backtest: result, messages: messages.filter((m) => !m.streaming), inputs_sig: resultSig,
            optimizer: optimizerRef.current, income_target: plannerRef.current,
          }, res.data.id);
        }
        nav(`/portfolio/${res.data.id}`, { replace: true });
      }
      // Clear the anonymous-portfolio stash — the visitor has successfully
      // saved a real portfolio, so we no longer need the localStorage copy.
      try { localStorage.removeItem("dp_anon_portfolio"); } catch (_) {}
      toast.success("Saved");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Save failed";
      if (status === 402) {
        toast.error(detail, {
          action: { label: "Upgrade", onClick: () => nav("/pricing") },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    } finally { setSaving(false); }
  };

  const explain = async () => {
    if (!result) return;
    setExplaining(true); setMessages([]);
    try {
      const res = await api.post("/explain", {
        portfolio_id: id || null,
        context: { summary: result.summary, contributions: result.contributions },
        question: `Explain the results of this ${form.drip ? "DRIP" : "cash dividend"} portfolio to a beginner investor. Highlight the role of dividends and DRIP.`,
      });
      setMessages([{ role: "assistant", content: "", streaming: true }]);
      streamIntoLastMessage(res.data.explanation);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "AI explanation failed";
      if (status === 402) {
        if (!user) {
          // Anon quota exhausted → nudge to account creation, preserving portfolio state.
          toast.error(detail, {
            action: {
              label: "Create free account",
              onClick: () => nav(`/auth?mode=register&next=${encodeURIComponent(window.location.pathname + window.location.search)}&intent=ai-quota`),
            },
            duration: 10000,
          });
        } else {
          toast.error(detail, {
            action: { label: "Upgrade", onClick: () => nav("/pricing") },
            duration: 8000,
          });
        }
      } else {
        toast.error(detail);
      }
    } finally { setExplaining(false); }
  };

  const askFollowUp = async () => {
    const q = followUp.trim();
    if (!q || !result || messages.length === 0) return;
    const newHistory = [...messages, { role: "user", content: q }];
    setMessages(newHistory);
    setFollowUp("");
    setExplaining(true);
    try {
      const res = await api.post("/explain", {
        portfolio_id: id || null,
        context: { summary: result.summary, contributions: result.contributions },
        question: q,
        history: newHistory.slice(0, -1), // history = everything BEFORE the current question
      });
      setMessages([...newHistory, { role: "assistant", content: "", streaming: true }]);
      streamIntoLastMessage(res.data.explanation);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Follow-up failed");
      setMessages(messages); // rollback on failure
    } finally { setExplaining(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* --------- Left: Builder --------- */}
      <div className="lg:col-span-5 min-w-0">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Portfolio</div>
        <h1 className="serif text-4xl mt-2">{id ? "Edit" : "Build"} your scenario</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Add tickers, set weights, choose a date range and initial investment. We&apos;ll import prices + dividends automatically.
        </p>

        <Card className="mt-6">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label htmlFor="pname">Name</Label>
              <Input
                id="pname"
                data-testid={TEST_IDS.builderName}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="pdesc">Description</Label>
              <Textarea
                id="pdesc"
                data-testid={TEST_IDS.builderDescription}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="init">Initial investment ($)</Label>
                <Input
                  id="init"
                  type="number"
                  min={100}
                  data-testid={TEST_IDS.builderInitial}
                  value={form.initial_investment}
                  onChange={(e) => setForm({ ...form, initial_investment: e.target.value })}
                />
              </div>
              <div className="flex items-end justify-between rounded-md border border-border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Reinvest dividends (DRIP)</div>
                  <div className="text-xs text-muted-foreground">Compound your income</div>
                </div>
                <Switch
                  data-testid={TEST_IDS.builderDrip}
                  checked={!!form.drip}
                  onCheckedChange={(v) => setForm({ ...form, drip: v })}
                />
              </div>
            </div>

            {/* Additional contributions row — sits directly below Initial
                investment so the "how much am I really putting in?" story
                reads top-to-bottom. Setting the amount to 0 (or leaving it
                blank) yields a pure lump-sum backtest, identical to before. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="addl">Additional contributions ($)</Label>
                <Input
                  id="addl"
                  type="number"
                  min={0}
                  step="any"
                  placeholder="0"
                  data-testid="builder-additional-contribution"
                  value={form.additional_contribution}
                  onChange={(e) => setForm({ ...form, additional_contribution: e.target.value })}
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Dollars added on top of your initial investment each period
                </div>
              </div>
              <div>
                <Label htmlFor="freq">Frequency</Label>
                <Select
                  value={form.contribution_frequency}
                  onValueChange={(v) => setForm({ ...form, contribution_frequency: v })}
                >
                  <SelectTrigger id="freq" data-testid="builder-contribution-frequency">
                    <SelectValue placeholder="Monthly" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground mt-1">
                  How often to contribute
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pstart">Start date</Label>
                <Input
                  id="pstart"
                  type="date"
                  data-testid={TEST_IDS.builderStart}
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="pend">End date</Label>
                <Input
                  id="pend"
                  type="date"
                  data-testid={TEST_IDS.builderEnd}
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            </div>

            <Separator />

            <div>
              <div className="flex items-center justify-between">
                <Label>Holdings</Label>
                <span className={`mono text-xs ${weightsValid ? "text-primary" : "text-destructive"}`}>
                  Total {totalWeight.toFixed(2)}%
                </span>
              </div>
              <div className="mt-2 flex gap-2">
                <div className="flex-1">
                  <TickerAutocomplete
                    testId={TEST_IDS.builderAddTicker}
                    value={newTicker}
                    onChange={setNewTicker}
                    onSelect={(row) => {
                      // Immediate add on suggestion click.
                      const t = row.ticker.trim().toUpperCase();
                      if (!t) return;
                      if (form.holdings.some((h) => h.ticker === t)) {
                        toast.error("Already added");
                        setNewTicker("");
                        return;
                      }
                      const next = balanceTo100([...form.holdings, { ticker: t, weight: 0 }]);
                      setForm({ ...form, holdings: next });
                      setNewTicker("");
                    }}
                    placeholder="Search dividend tickers (e.g. SCHD, JEPI, VYM)"
                  />
                </div>
                <Button data-testid={TEST_IDS.builderAddTickerButton} onClick={addTicker} variant="secondary" className="shrink-0">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              <div className="mt-3 space-y-2">
                {form.holdings.map((h, i) => (
                  <div key={h.ticker} data-testid={TEST_IDS.builderHoldingRow} className="rounded-md border border-border px-3 py-2 bg-card">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="mono uppercase text-xs">{h.ticker}</Badge>
                      <div className="flex-1" />
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        value={h.weight}
                        onChange={(e) => setWeight(i, e.target.value)}
                        data-testid={TEST_IDS.builderHoldingWeight}
                        className="w-24 mono text-right"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                      <button
                        onClick={() => removeHolding(i)}
                        data-testid={TEST_IDS.builderRemoveHolding}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <Slider
                      data-testid="builder-holding-slider"
                      value={[Number(h.weight) || 0]}
                      onValueChange={(v) => setWeightSlider(i, v[0])}
                      min={0}
                      max={100}
                      step={1}
                      className="mt-2"
                      aria-label={`${h.ticker} weight`}
                    />
                  </div>
                ))}
                {form.holdings.length === 0 && (
                  <div className="text-sm text-muted-foreground italic">No holdings yet — add a ticker above.</div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                data-testid={TEST_IDS.builderRunBacktest}
                onClick={runBacktest}
                disabled={running || !weightsValid}
                className="rounded-full"
              >
                {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <><PlayCircle className="w-4 h-4 mr-2" /> Run backtest</>}
              </Button>
              <Button
                data-testid={TEST_IDS.builderSave}
                onClick={save}
                disabled={saving || !weightsValid}
                variant="outline"
                className="rounded-full"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --------- Right: Results --------- */}
      <div className="lg:col-span-7 space-y-6">
        {!result && !running && !runError && (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <div className="w-14 h-14 rounded-full bg-muted grid place-items-center mx-auto text-muted-foreground">
                <TrendingUp className="w-6 h-6" />
              </div>
              <h3 className="serif text-3xl mt-4">Waiting for your first backtest.</h3>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                Click <span className="mono">Run backtest</span> to import historical data and simulate this portfolio.
              </p>
            </CardContent>
          </Card>
        )}

        {running && (
          <Card data-testid="backtest-running">
            <CardContent className="p-10 text-center">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary" />
              <h3 className="serif text-2xl mt-4">Running your backtest…</h3>
              {/* Indeterminate progress bar — real backtest usually 3-8s;
                  animated bar tells users the app is alive instead of a
                  spinner-only "did it freeze?" moment. */}
              <div className="mt-5 max-w-sm mx-auto h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full w-1/3 bg-primary rounded-full animate-[backtestbar_1.6s_ease-in-out_infinite]" />
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                Downloading 30+ years of prices and dividends · simulating DRIP
              </p>
              <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-2">
                Typically 3–8 seconds
              </p>
            </CardContent>
          </Card>
        )}

        {runError && !running && (
          <Card className="border-destructive/40 bg-destructive/[0.03]" data-testid="backtest-error">
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-destructive/10 grid place-items-center mx-auto text-destructive">
                <RotateCcw className="w-5 h-5" />
              </div>
              <h3 className="serif text-2xl mt-4">Backtest failed.</h3>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto break-words" data-testid="backtest-error-msg">{runError}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Button
                  onClick={() => runBacktest()}
                  className="rounded-full"
                  data-testid="backtest-retry"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Retry
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setRunError(null)}
                  className="rounded-full"
                >
                  Dismiss
                </Button>
              </div>
              <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-4">
                If the retry keeps failing, one of your tickers may not have historical data on that date range.
              </p>
            </CardContent>
          </Card>
        )}

        {result && (
          <>
            <div ref={reportRef} className="space-y-6">
            {/* Branded PDF cover — hidden on screen, revealed only during PDF export */}
            <div
              ref={coverRef}
              className="pdf-cover"
              style={{ display: "none" }}
              data-testid="pdf-cover"
            >
              <div style={{
                minHeight: "265mm",
                padding: "24mm 20mm",
                backgroundColor: "#F7F5F0",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                fontFamily: "Georgia, 'Times New Roman', serif",
              }}>
                <div>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "10px",
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    color: "#8B7355",
                  }}>
                    Dividend Portfolio Planner
                  </div>
                  <div style={{
                    marginTop: "8mm",
                    fontSize: "34pt",
                    lineHeight: "1.1",
                    color: "#1F2937",
                    fontWeight: 600,
                  }}>
                    DPP
                  </div>
                  <div style={{
                    marginTop: "2mm",
                    fontSize: "11pt",
                    color: "#6B7280",
                  }}>
                    Historical backtest & dividend analysis
                  </div>
                </div>

                <div style={{ borderTop: "1px solid #D1CDBF", paddingTop: "8mm" }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "9px",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: "#8B7355",
                  }}>
                    Portfolio
                  </div>
                  <div style={{
                    marginTop: "3mm",
                    fontSize: "28pt",
                    lineHeight: "1.15",
                    color: "#111827",
                  }}>
                    {form.name || "Untitled portfolio"}
                  </div>
                  {form.description && (
                    <div style={{
                      marginTop: "4mm",
                      fontSize: "11pt",
                      color: "#4B5563",
                      fontStyle: "italic",
                      maxWidth: "150mm",
                    }}>
                      {form.description}
                    </div>
                  )}
                  <div style={{ marginTop: "10mm", display: "flex", gap: "12mm", flexWrap: "wrap" }}>
                    <CoverField label="Range" value={`${form.start_date} → ${form.end_date}`} />
                    <CoverField label="Initial" value={fmt$(form.initial_investment)} />
                    <CoverField label="DRIP" value={form.drip ? "ON" : "OFF"} />
                    <CoverField label="Holdings" value={String(form.holdings.length)} />
                  </div>
                  {result && (
                    <div style={{ marginTop: "8mm", display: "flex", gap: "12mm", flexWrap: "wrap" }}>
                      <CoverField label="Final value" value={fmt$(result.summary.final_value)} highlight />
                      <CoverField label="Total return" value={fmtPct(result.summary.total_return_pct)} highlight />
                      <CoverField label="CAGR" value={fmtPct(result.summary.cagr_pct)} highlight />
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ borderTop: "1px solid #D1CDBF", paddingTop: "6mm" }}>
                    <div style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "9px",
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: "#8B7355",
                      marginBottom: "3mm",
                    }}>
                      Disclaimer
                    </div>
                    <div style={{
                      fontSize: "9pt",
                      color: "#4B5563",
                      lineHeight: "1.5",
                      fontFamily: "Georgia, 'Times New Roman', serif",
                    }}>
                      This report is generated by Dividend Portfolio Planner (DPP) for informational and
                      educational purposes only. It is a hypothetical backtest based on historical price and
                      dividend data from market data providers (Financial Modeling Prep, with Yahoo Finance as a backup) and does not represent actual investment
                      results. Past performance does not guarantee future results. Nothing in this document
                      constitutes financial, investment, or tax advice — please consult a licensed advisor
                      before making investment decisions.
                    </div>
                    <div style={{
                      marginTop: "6mm",
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "8pt",
                      color: "#8B7355",
                    }}>
                      Generated {new Date().toLocaleString()} · dividendportfolioplanner.com
                    </div>
                  </div>
                </div>
              </div>
              <div className="pdf-page-break" />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-secondary">Backtest report</div>
                <h2 className="serif text-3xl">{form.name || "Portfolio"} · {result.summary.years}y</h2>
              </div>
              <div className="flex gap-2 print:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={exportResultsCsv}
                  data-testid="results-export-csv"
                >
                  <Download className="w-4 h-4 mr-1.5" /> CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={isProPlan ? exportReportPdf : () => { toast.message("Branded PDF reports are a Pro feature"); nav("/pricing"); }}
                  disabled={downloadingPdf}
                  data-testid="results-export-pdf"
                >
                  {downloadingPdf
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Building PDF…</>
                    : isProPlan
                      ? <><FileText className="w-4 h-4 mr-1.5" /> PDF report</>
                      : <><Lock className="w-3.5 h-3.5 mr-1.5" /> PDF report · Pro</>}
                </Button>
              </div>
            </div>
            {result.cached && (
              <div className="flex items-center justify-between rounded-md border border-primary/20 bg-primary/5 px-4 py-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-primary text-primary-foreground">Cached</span>
                  <span className="text-muted-foreground">
                    Instant result — computed {result.computed_at ? new Date(result.computed_at).toLocaleString() : ""}
                  </span>
                </div>
                <button
                  data-testid="backtest-refresh"
                  onClick={() => runBacktest({ refresh: true })}
                  disabled={running}
                  className="mono text-xs text-primary hover:underline underline-offset-2 disabled:opacity-50"
                >
                  Recompute
                </button>
              </div>
            )}

            {result.warnings?.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="results-warnings">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <ul className="space-y-1">{result.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
              </div>
            )}

            {resultsStale && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-secondary/40 bg-secondary/[0.06] px-4 py-3 text-sm" data-testid="results-stale-banner">
                <span>These saved results were calculated before your latest changes to the portfolio inputs.</span>
                <Button size="sm" className="rounded-full shrink-0" onClick={() => runBacktest()} disabled={running} data-testid="results-stale-rerun">Re-run backtest</Button>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="backtest-metrics">
              <Metric
                testId="metric-invested"
                icon={Coins}
                label={result.summary.total_contributions ? "Total invested" : "Original investment"}
                value={fmt$(result.summary.total_invested || result.summary.initial_investment)}
                subtitle={(() => {
                  const base = result.summary.total_invested || result.summary.initial_investment;
                  const g = result.summary.final_value - base;
                  const extra = result.summary.total_contributions
                    ? ` (incl. ${fmt$(result.summary.total_contributions)} contribs)`
                    : "";
                  return `${g >= 0 ? "+" : "−"}${fmt$(Math.abs(g))} ${g >= 0 ? "gain" : "loss"}${extra}`;
                })()}
              />
              <Metric testId={TEST_IDS.metricFinalValue} icon={Wallet} label="Final value" value={fmt$(result.summary.final_value)} accent="primary" />
              <Metric testId={TEST_IDS.metricTotalReturn} icon={TrendingUp} label="Total return" value={fmtPct(result.summary.total_return_pct)} />
              <Metric testId={TEST_IDS.metricCagr} icon={Percent} label="CAGR" value={fmtPct(result.summary.cagr_pct)} />
              <Metric testId={TEST_IDS.metricDividends} icon={Coins} label="Total dividends" value={fmt$(result.summary.total_dividends)} accent="secondary" />
              <Metric testId={TEST_IDS.metricYoc} icon={DollarSign} label="Yield on cost" value={fmtPct(result.summary.yield_on_cost_pct)} accent="secondary" subtitle="TTM dividends ÷ amount invested" />
              <Metric testId="metric-current-yield" icon={Percent} label="Current yield" value={fmtPct(incomeBasis(result.summary).currentYield)} accent="secondary" subtitle="TTM dividends ÷ today's value" />
              <Metric
                testId="metric-monthly-income"
                icon={Calendar}
                label="Est. monthly income"
                value={fmt$2(incomeBasis(result.summary).monthly)}
                accent="secondary"
                subtitle="last 12 months of dividends ÷ 12"
              />
            </div>

            <IncomeTargetCard
              portfolioId={id}
              summary={result.summary}
              drip={!!form.drip}
              initial={plannerRef.current}
              seedVersion={seedVersion}
              onChange={(state) => { plannerRef.current = state; saveWorkspace({ income_target: state }); }}
              onApplyLumpSum={(amt) => applyPlannerAmount({ initial_investment: Math.round(amt) }, `Initial investment ${fmt$(Math.round(amt))}`)}
              onApplyMonthly={(m) => applyPlannerAmount({ additional_contribution: Math.round(m * 100) / 100, contribution_frequency: "monthly" }, `Monthly contribution ${fmt$2(m)}`)}
              applying={applyingPlan || running}
            />

            <OptimizerCard
              form={form}
              portfolioId={id}
              initialSuggestion={optimizerRef.current}
              seedVersion={seedVersion}
              onSuggestionChange={(s) => { optimizerRef.current = s; saveWorkspace({ optimizer: s }); }}
              onApply={async (proposal) => {
                const weights = proposal.proposed.weights;
                const currentSummary = result?.summary;
                // Optional baseline snapshot on the FIRST AI apply, so users can revert to "before AI".
                if (id && snapshots.length === 0 && currentSummary) {
                  try {
                    await api.post(`/portfolios/${id}/snapshots`, {
                      label: "Baseline (before AI)",
                      source: "baseline",
                      holdings: form.holdings.map((h) => ({ ticker: h.ticker, weight: Number(h.weight) })),
                      initial_investment: Number(form.initial_investment),
                      start_date: form.start_date,
                      end_date: form.end_date,
                      drip: !!form.drip,
                      summary: currentSummary,
                    });
                  } catch { /* non-fatal */ }
                }
                // Snapshot the applied AI weights.
                if (id) {
                  try {
                    await api.post(`/portfolios/${id}/snapshots`, {
                      label: `AI · Max ${proposal.goal === "yield" ? "yield" : proposal.goal === "cagr" ? "CAGR" : "balanced"}`,
                      source: "ai_optimizer",
                      goal: proposal.goal,
                      rationale: proposal.rationale,
                      holdings: weights,
                      initial_investment: Number(form.initial_investment),
                      start_date: form.start_date,
                      end_date: form.end_date,
                      drip: !!form.drip,
                      summary: proposal.proposed.summary,
                    });
                    await loadSnapshots();
                  } catch { /* non-fatal */ }
                }
                setForm((prev) => ({
                  ...prev,
                  holdings: prev.holdings.map((h) => {
                    const found = weights.find((w) => w.ticker === h.ticker);
                    return found ? { ...h, weight: Number(found.weight) } : h;
                  }),
                }));
                toast.success(id
                  ? "Optimizer applied · saved to history"
                  : "Optimizer applied — save this portfolio to keep the history");
              }}
            />

            {id && snapshots.length > 0 && (
              <SnapshotHistory
                snapshots={snapshots}
                onRestore={async (sid) => {
                  try {
                    const res = await api.post(`/portfolios/${id}/snapshots/${sid}/restore`);
                    setForm(res.data);
                    await loadSnapshots();
                    toast.success("Snapshot restored — run backtest to see results");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Restore failed");
                  }
                }}
                onDelete={async (sid) => {
                  if (!window.confirm("Delete this snapshot? This can't be undone.")) return;
                  try {
                    await api.delete(`/portfolios/${id}/snapshots/${sid}`);
                    await loadSnapshots();
                    toast.success("Snapshot deleted");
                  } catch {
                    toast.error("Delete failed");
                  }
                }}
              />
            )}

            <Card className="bg-muted/40 border-primary/10 animate-draw">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5" /> AI Explanation
                    </div>
                    <h3 className="serif text-2xl mt-2">What this backtest is telling you</h3>
                  </div>
                  <Button
                    data-testid={TEST_IDS.aiExplainButton}
                    onClick={explain}
                    disabled={explaining}
                    className="rounded-full"
                  >
                    {explaining && messages.length === 0
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Thinking…</>
                      : messages.length === 0
                      ? "Explain results"
                      : "Regenerate"}
                  </Button>
                </div>

                {messages.length === 0 && !explaining && (
                  <p className="mt-4 text-sm text-muted-foreground">
                    Click <span className="mono">Explain results</span> to get a plain-English breakdown of what happened, powered by Claude Sonnet 4.6.
                  </p>
                )}

                {messages.length > 0 && (
                  <div className="mt-4 space-y-3" data-testid={TEST_IDS.aiExplainText}>
                    {messages.map((m, idx) => (
                      <div
                        key={idx}
                        data-testid={`ai-msg-${m.role}`}
                        className={m.role === "user"
                          ? "ml-8 rounded-lg bg-primary/10 border border-primary/20 px-4 py-2 text-sm"
                          : "group relative rounded-lg bg-card border border-border px-4 py-3 text-sm leading-relaxed"}
                      >
                        {m.role === "user" && (
                          <div className="mono text-[10px] uppercase tracking-widest text-primary/70 mb-1">You</div>
                        )}
                        {m.role === "assistant"
                          ? <div className="[&>p:first-child]:mt-0" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content || "", { compact: true }) }} />
                          : <span className="whitespace-pre-wrap">{m.content}</span>}
                        {m.role === "assistant" && m.streaming && (
                          <span className="inline-block w-1.5 h-4 bg-primary/70 align-middle ml-0.5 animate-pulse" aria-hidden />
                        )}
                        {m.role === "assistant" && !m.streaming && m.content && (
                          <button
                            data-testid="ai-msg-copy"
                            onClick={() => copyMessage(m.content)}
                            className="absolute top-2 right-2 p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 opacity-0 group-hover:opacity-100 transition-all"
                            aria-label="Copy message"
                            title="Copy"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    {explaining && (
                      <div className="rounded-lg bg-card border border-border px-4 py-3 text-sm flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Thinking…
                      </div>
                    )}
                  </div>
                )}

                {messages.length > 0 && !isProPlan && (
                  <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="explain-save-pro-hint">
                    <Lock className="w-3 h-3" /> This chat isn&apos;t saved.{" "}
                    <Link to="/pricing" className="text-primary font-semibold underline underline-offset-2">Pro</Link> keeps AI explanation chats with every portfolio.
                  </p>
                )}
                {messages.length > 0 && (
                  <form
                    onSubmit={(e) => { e.preventDefault(); askFollowUp(); }}
                    className="mt-4 flex gap-2"
                  >
                    <Input
                      value={followUp}
                      onChange={(e) => setFollowUp(e.target.value)}
                      placeholder='Ask a follow-up: "what if I remove JEPI?"'
                      data-testid="ai-followup-input"
                      disabled={explaining}
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      data-testid="ai-followup-send"
                      disabled={explaining || !followUp.trim()}
                      className="rounded-full shrink-0"
                    >
                      Ask
                    </Button>
                  </form>
                )}

                {messages.length > 0 && followUpSuggestions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {followUpSuggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setFollowUp(s)}
                        disabled={explaining}
                        data-testid="ai-followup-suggestion"
                        className="mono text-[11px] px-2 py-1 rounded-full bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors disabled:opacity-50"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid={TEST_IDS.chartValue} className="animate-draw">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Portfolio value over time</div>
                    <h3 className="serif text-2xl">{form.name || "Portfolio"} · {result.summary.years}y</h3>
                  </div>
                  <Badge variant="outline" className="mono">{result.summary.drip ? "DRIP ON" : "DRIP OFF"}</Badge>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={result.series}>
                    <defs>
                      <linearGradient id="gradVal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} minTickGap={40} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => fmt$2(v)}
                    />
                    <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#gradVal)" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid={TEST_IDS.chartDividends} className="animate-draw">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Cumulative dividends</div>
                    <h3 className="serif text-2xl">Income earned over time</h3>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={result.series}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} minTickGap={40} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => fmt$2(v)}
                    />
                    <Line type="monotone" dataKey="cumulative_dividends" stroke="hsl(var(--secondary))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid={TEST_IDS.contributionsTable}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="serif text-2xl">By ticker</h3>
                  <span className="mono text-xs text-muted-foreground">Contribution breakdown</span>
                </div>
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60">
                      <tr className="text-left">
                        <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground">Ticker</th>
                        <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground">Weight</th>
                        <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">Shares</th>
                        <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">Current value</th>
                        <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">Total dividends</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.contributions.map((c) => (
                        <tr key={c.ticker} className="border-t border-border">
                          <td className="px-4 py-2 mono font-semibold">{c.ticker}</td>
                          <td className="px-4 py-2 mono">{c.weight}%</td>
                          <td className="px-4 py-2 mono text-right">{c.shares.toLocaleString()}</td>
                          <td className="px-4 py-2 mono text-right">{fmt$2(c.current_value)}</td>
                          <td className="px-4 py-2 mono text-right text-secondary">{fmt$2(c.total_dividends)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            </div>
          </>
        )}

        {/* Post-signup trial offer — 2026-02-13. Fires once per account when
            the visitor lands back on /portfolio/new?intent=save-portfolio
            with a valid session, giving them a one-click nudge to try Core. */}
        <Dialog open={showTrialOffer} onOpenChange={setShowTrialOffer}>
          <DialogContent className="max-w-md" data-testid="trial-offer-dialog">
            <DialogHeader>
              <DialogTitle className="serif text-2xl">Want to compare more strategies?</DialogTitle>
              <DialogDescription className="pt-1 text-sm leading-relaxed">
                Start your <b>7-day Core trial</b> to unlock up to 10 saved portfolios, 10 AI actions per month (Optimizer + Income Planner + Thesis), and SnapTrade brokerage sync. No credit card required — cancel anytime and keep everything you build on the Free plan.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 rounded-md border border-secondary/40 bg-secondary/5 px-4 py-3">
              <div className="mono text-[10px] uppercase tracking-widest text-secondary mb-1">What you get during the trial</div>
              <ul className="text-xs space-y-1 text-foreground/85">
                <li>· Up to <b>10</b> saved portfolios (vs 1 on Free)</li>
                <li>· <b>10</b> AI actions / month (vs 4 on Free) — Optimizer, Income Planner &amp; ticker thesis</li>
                <li>· <b>SnapTrade</b> read-only brokerage sync</li>
                <li>· Priority backtest queue on peak-load days</li>
              </ul>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setShowTrialOffer(false)}
                data-testid="trial-offer-dismiss"
              >
                Maybe later
              </Button>
              <Button
                className="rounded-full bg-secondary text-secondary-foreground hover:brightness-110"
                onClick={async () => {
                  try {
                    await api.post("/subscription/start-trial");
                    toast.success("7-day Core trial activated — enjoy!");
                    setShowTrialOffer(false);
                    // Refresh the page so /me pulls the new subscription
                    // state and the builder unlocks its Core-tier ceilings.
                    setTimeout(() => window.location.reload(), 600);
                  } catch (e) {
                    // If the trial fails to start (e.g. previously used),
                    // fall back to the pricing page so the user can pick
                    // another plan without hitting a dead end.
                    toast.error(e?.response?.data?.detail || "Could not start trial");
                    nav("/pricing?trial=core");
                  }
                }}
                data-testid="trial-offer-start"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Start 7-day Core trial
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon, accent, testId, subtitle }) {
  const accentCls =
    accent === "primary" ? "text-primary" :
    accent === "secondary" ? "text-secondary" : "text-foreground";
  // Auto-shrink font size so multi-digit values (e.g. "$12,345,678", "153.47%")
  // stay inside the card at all viewport widths. Same breakpoints as the
  // Calculator's Stat component.
  const len = String(value ?? "").length;
  const valueSize =
    len <= 7  ? "text-2xl sm:text-3xl" :
    len <= 10 ? "text-xl sm:text-2xl"  :
    len <= 13 ? "text-lg sm:text-xl"   :
                "text-base sm:text-lg";
  return (
    <Card data-testid={testId} className="card-hover min-w-0">
      <CardContent className="p-4 min-w-0">
        <div className="flex items-start gap-2 text-muted-foreground">
          <Icon className="w-4 h-4 shrink-0" />
          <span className="mono text-[10px] uppercase tracking-widest leading-tight">{label}</span>
        </div>
        <div
          className={`serif mt-2 tabular-nums leading-tight whitespace-nowrap ${valueSize} ${accentCls}`}
          title={String(value ?? "")}
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {subtitle && (
          <div className="mono text-[10px] text-secondary mt-1 leading-snug">{subtitle}</div>
        )}
      </CardContent>
    </Card>
  );
}


function CoverField({ label, value, highlight }) {
  return (
    <div>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "8pt",
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: "#8B7355",
      }}>
        {label}
      </div>
      <div style={{
        marginTop: "1.5mm",
        fontSize: highlight ? "16pt" : "12pt",
        color: highlight ? "#8B7355" : "#111827",
        fontFamily: highlight ? "Georgia, 'Times New Roman', serif" : "'JetBrains Mono', monospace",
        fontWeight: highlight ? 600 : 400,
      }}>
        {value}
      </div>
    </div>
  );
}
